import { bulkUploadTransactions } from '@commenda/api';
import {
  SupportedDateFormatforCsvMapping,
  TaxCalculationType,
  TransactionTemplateFormatEnum,
  TransactionTemplateImportTypeEnum,
} from '@commenda/api/sales-tax/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { SingleFileUploader } from '@commenda/kakashi/src/components/SingleFileUploader';
import {
  readFileColumns,
  uploadFileToS3,
} from '@commenda/kakashi/src/components/SingleFileUploader/utils';
import { FileBasic } from '@commenda/types/src/file/types';
import { TSetNotification } from '@commenda/types/src/random';
import { useState } from 'react';

import TransactionTypeCard from '../common/TransactionTypeCard';
import { TaxCalculationSettingModal } from './TaxCalculationSettingModal';
import TransactionsColumnMappingModal from './transactions-column-mapping';

import { LegacyDocumentType } from '@prisma/client';
import { downloadSalesTaxTransactionsTemplate } from '../../common/utils/salesTaxCsvTemplate';
import {
  transformCsvForDeGb,
  TRANSACTION_IMPORT_TYPES,
  TRANSACTION_TEMPLATE,
  TRANSACTION_TEMPLATE_FORMATS,
} from '../utils';

interface SalesTaxImportTransactionsModalProps {
  isTransactionsModalShown: boolean;
  setIsTransactionsModalShown: (isTransactionsModalShown: boolean) => void;
  corporationId: number;
  setNotification: TSetNotification;
  onSuccess: () => Promise<void> | void;
  companyId: number;
  /** ISO 3166-1 country code for the corporation's incorporation country */
  incorporationCountry?: string;
  /** Whether to use the EU/UK upload flow (simplified flow without template selection) */
  isEuUploadFlow?: boolean;
  /**
   * When true, the manual upload pins reporting_date_override to the transaction date
   * (BUG-1074). No UI element. Enabled for Global Indirect Tax; off for legacy US sales tax.
   */
  copyTransactionDateToReportingDate?: boolean;
}

const SalesTaxImportTransactionsModal = ({
  isTransactionsModalShown,
  setIsTransactionsModalShown,
  corporationId,
  setNotification,
  onSuccess,
  companyId,
  isEuUploadFlow = false,
  copyTransactionDateToReportingDate = false,
}: SalesTaxImportTransactionsModalProps) => {
  const [file, setFile] = useState<FileBasic | null>(null);
  // Store the original File object for DE/GB transformation after mapping
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTaxInclusiveTransactions, setIsTaxInclusiveTransactions] =
    useState(false);

  const taxCalculationSettingModal = useToggle();

  const [
    showTransactionsMappingUploadModal,
    setShowTransactionsMappingUploadModal,
  ] = useState(false);

  const [csvColumns, setCsvColumns] = useState<string[]>([]);

  const onUploadTransactions = async () => {
    setIsTransactionsModalShown(false);
    setShowTransactionsMappingUploadModal(true);
  };

  const handleBack = () => {
    setShowTransactionsMappingUploadModal(false);
    setIsTransactionsModalShown(true);
  };

  const onUploadTransactionsWithMapping = async (
    mapping: Record<string, string>,
    dateFormat: SupportedDateFormatforCsvMapping
  ) => {
    if (!file) return;

    const columnMapping = Object.entries(mapping).map(
      ([toColumn, fromColumn]) => ({
        fromColumn,
        toColumn,
      })
    );

    setIsSubmitting(true);

    try {
      let fileToUpload = file;

      // For DE/GB countries, transform the CSV to combine metadata fields into a JSON column
      if (isEuUploadFlow && originalFile) {
        const transformedFile = await transformCsvForDeGb(originalFile, columnMapping);
        console.log({ transformedFile })
        const uploadResult = await uploadFileToS3(transformedFile, companyId, undefined);
        if (!uploadResult) {
          throw new Error('Failed to upload transformed file');
        }
        fileToUpload = {
          fileName: uploadResult.fileName,
          fileUrl: uploadResult.fileUrl,
          documentType: LegacyDocumentType.Other,
        };
      }

      const res = await bulkUploadTransactions(
        {
          file: fileToUpload,
          corporationId,
          columnMapping,
          dateFormat,
          isTaxInclusiveTransactions,
          defaultQuantity: 1,
          copyTransactionDateToReportingDate,
        },
        undefined
      );

      if (!res.success) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: res.error,
        });
        return;
      }

      await onSuccess();
      setNotification({
        show: true,
        valid: true,
        title: 'Upload in Progress',
        message: 'Your transactions are being processed. You will be notified when complete.',
      });
    } finally {
      setIsSubmitting(false);
      setShowTransactionsMappingUploadModal(false);
      setFile(null);
      setOriginalFile(null);
    }
  };

  const onFileUpload = async (fl: File, cmpId: number, token: string) => {
    try {
      // Store the original file for DE/GB transformation after mapping
      if (isEuUploadFlow) {
        setOriginalFile(fl);
      }

      const columns = await readFileColumns(fl);
      setCsvColumns(columns);

      const uploadResult = await uploadFileToS3(fl, cmpId, token);
      if (!uploadResult) {
        throw new Error('Failed to upload file to S3');
      }

      return uploadResult;
    } catch (err) {
      setError('Failed to process CSV file');
      throw err;
    }
  };

  const handleFileUpload = async (fileBasic: FileBasic) => {
    setFile(fileBasic);
    setError('');
  };

  const handleCloseBulkUploadModal = () => {
    setIsTransactionsModalShown(false);
    setFile(null);
    setError('');
  };

  const templateFormat = TransactionTemplateFormatEnum.ADVANCED

  const [transactionImportType, setTransactionImportType] =
    useState<TransactionTemplateImportTypeEnum>(
      TransactionTemplateImportTypeEnum.INVOICES
    );

  const handleDownloadTemplate = () => {
    const selectedTemplate =
      TRANSACTION_TEMPLATE[`${transactionImportType}-${templateFormat}`];

    try {
      downloadSalesTaxTransactionsTemplate(selectedTemplate);

      setTimeout(() => {
        setNotification({
          show: true,
          valid: true,
          title: `Transactions template for ${selectedTemplate.text}`,
          message: 'Template downloaded successfully',
        });
      }, 2000);
    } catch (err) {
      setNotification({
        show: true,
        valid: false,
        title: 'Transactions template download Failed',
        message:
          err instanceof Error ? err.message : 'Failed to download template',
      });
    }
  };

  return (
    <>
      <TransactionsColumnMappingModal
        isOpen={showTransactionsMappingUploadModal}
        onClose={() => setShowTransactionsMappingUploadModal(false)}
        onUpload={onUploadTransactionsWithMapping}
        csvColumnsFromUpload={csvColumns}
        fileName={file?.fileName || ''}
        disabled={!file || isSubmitting}
        handleBack={handleBack}
        templateFormat={templateFormat}
        transactionImportType={transactionImportType}
        openTaxCalculationSettingModal={taxCalculationSettingModal.show}
        isTaxInclusiveTransactions={isTaxInclusiveTransactions}
        isEuUploadFlow={isEuUploadFlow}
      />

      <TaxCalculationSettingModal
        isShown={taxCalculationSettingModal.visible}
        onClose={taxCalculationSettingModal.hide}
        onSave={(type) => {
          setIsTaxInclusiveTransactions(
            type === TaxCalculationType.TAX_INCLUSIVE
          );
          taxCalculationSettingModal.hide();
        }}
        defaultType={
          isTaxInclusiveTransactions
            ? TaxCalculationType.TAX_INCLUSIVE
            : TaxCalculationType.TAX_EXCLUSIVE
        }
      />

      <Dialog open={isTransactionsModalShown}>
        <DialogContent
          className="bg-white min-w-[650px] flex flex-col"
          hideCloseIcon
          preventCloseOnOutsideClick
        >
          <DialogHeader className="flex items-start flex-col gap-1">
            <DialogTitle className="flex-start">
              Import Transaction Data
            </DialogTitle>
            <p className="txt-small-regular text-text-tertiary">
              Upload a CSV or Excel file to import your transaction records.
            </p>
          </DialogHeader>
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
              {/* For DE/GB countries, skip template selection and show only file upload */}
              {!isEuUploadFlow && (
                <>
                  <div className="flex flex-col gap-3">
                    <div className="txt-medium-bold">
                      What would you like to import?
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {TRANSACTION_IMPORT_TYPES.map((type) => (
                        <TransactionTypeCard
                          key={type.id}
                          title={type.title}
                          description={type.description}
                          selected={transactionImportType === type.id}
                          onClick={() => setTransactionImportType(type.id)}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="txt-medium-bold">Upload your file</div>
                  {!isEuUploadFlow && (
                    <div
                      className="txt-small-bold text-utility-blue-600 cursor-pointer flex items-center gap-2"
                      onClick={handleDownloadTemplate}
                    >
                      <Icons.Download01 className='size-4' />
                      {transactionImportType &&
                        templateFormat &&
                        TRANSACTION_TEMPLATE[
                        `${transactionImportType}-${templateFormat}`
                        ]
                        ? TRANSACTION_TEMPLATE[
                          `${transactionImportType}-${templateFormat}`
                        ].text
                        : 'Download template'}
                    </div>
                  )}
                </div>
                <SingleFileUploader
                  companyId={companyId}
                  file={file}
                  setFile={setFile}
                  error={error}
                  setError={setError}
                  onFileChange={handleFileUpload}
                  onFileUpload={onFileUpload}
                  onError={() => { }}
                  token={undefined}
                  documentType={LegacyDocumentType.Other}
                  maxSize={150 * 1024 * 1024} // 150MB
                  accept={{
                    'text/csv': ['.csv'],
                    'application/vnd.ms-excel': ['.csv', '.xls'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                    'application/csv': ['.csv'],
                  }}
                  message="Supported file formats: CSV, XLS, XLSX (Max size: 150MB)"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                size="sm"
                onClick={handleCloseBulkUploadModal}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onUploadTransactions}
                disabled={
                  !file ||
                  isSubmitting ||
                  // For DE/GB, only check file; for others, also check template settings
                  (!isEuUploadFlow && (!templateFormat || !transactionImportType))
                }
                isLoading={isSubmitting}
              >
                Continue to mapping
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SalesTaxImportTransactionsModal;
